import ContentAdd from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/CreateOutlined'
import EditOrImportButton from 'elements/button/EditOrImportButton'
import { List } from 'elements/react-admin/List'
import ListActions from 'elements/react-admin/ListActions'
import FullScreenLoading from 'layout/widgets/FullScreenLoading'
import { Switch } from 'opensolar-ui'
import { useDataProvider, useNotify, useRefresh } from 'ra-core'
import { useEffect, useState } from 'react'
import {
  BooleanField,
  Button,
  Datagrid,
  DeleteButton,
  FilterComp,
  FunctionField,
  Link,
  SelectInput,
  TextField,
} from 'react-admin'
import { styles as standardStyles } from 'styles'
import { doNotTranslate } from 'util/misc'
import { useCurrentStructType } from './hooks/useCurrentStructType'
import { SingularCreateButton } from './SingularCreateButton'
import { StructsBreadcrumbs } from './StructsBreadcrumbs'
import { AdStatus, AdStructTypes } from './types'

const StructListActions = ({ currentType, currentTypeId, singularStructTypes, ...props }) => {
  const isSingularList = currentTypeId === 'singular'

  // Reset filters to avoid all structs pages sharing the same filters (single-type list only)
  useEffect(() => {
    if (isSingularList) return
    if (currentType && props.filterValues.type !== currentType.id) {
      console.debug('Reset filters: ', { type: currentType.id }, props.filterValues)
      props.setFilters({ type: currentType.id }, undefined, false)
    }
  }, [props.filterValues.type, currentType?.id, isSingularList])

  return (
    <>
      {/*@ts-ignore*/}
      <ListActions {...props} hasCreate={false} exporter={false} hasArchived={!isSingularList} />
      {props.hasCreate &&
        (isSingularList ? (
          <SingularCreateButton singularStructTypes={singularStructTypes} {...props} />
        ) : (
          currentType && <StructCreateButton structType={currentType} {...props} />
        ))}
    </>
  )
}

const StructFilter = ({ structTypes, ...props }) => {
  return (
    <FilterComp {...props}>
      <SelectInput
        options={{ style: standardStyles.FILTER_FIELD_STYLE }}
        label="Completed"
        source="is_completed"
        allowEmpty={false}
        choices={[
          { id: 'false', name: 'Not Completed' },
          { id: 'true', name: 'Completed' },
        ]}
      />
    </FilterComp>
  )
}

const StructCreateButton = (props) => {
  const { icon = <ContentAdd />, id, onClick, structType } = props

  if (!structType) return null

  return (
    <Link
      id={id}
      style={{ textDecoration: 'none' }}
      to={{
        pathname: `/structs/${structType.id}/create`,
      }}
      onClick={onClick}
    >
      <Button
        id={id ? id + '-button' : undefined}
        variant="outlined"
        color="default"
        size="large"
        name={'raCreateButton'}
        label={doNotTranslate('Create ' + structType.title)}
      >
        {icon}
      </Button>
    </Link>
  )
}

const SingularRowActions = ({ record, ...props }) => (
  <div style={{ minWidth: 110 }}>
    <Link to={`/structs/${record.type}/${record.id}/edit`} style={{ textDecoration: 'none' }}>
      <Button
        label="Edit"
        color="inherit"
        style={{ color: '#4d4d4d', justifyContent: 'flex-start', margin: 0, padding: 6 }}
        onClick={() => {}}
      >
        <EditIcon />
      </Button>
    </Link>
    <DeleteButton
      undoable={false}
      record={record}
      resource="structs"
      basePath="/structs/singular"
      redirect="/structs/singular"
      style={{ color: '#4d4d4d', justifyContent: 'flex-start', margin: 0, padding: 6 }}
    />
  </div>
)

const getStatus = (record) => {
  const isCompleted = !!record.data?.top_level_fields?.is_completed
  const isPublished = record.is_published
  const isArchived = record.is_archived

  if (!isPublished && !isCompleted && !isArchived) return AdStatus.INACTIVE
  if (isPublished && !isCompleted && !isArchived) return AdStatus.PUBLISHED
  if (!isPublished && isCompleted && !isArchived) return AdStatus.COMPLETED
  if (!isPublished && isCompleted && isArchived) return AdStatus.COMPLETED
  if (!isPublished && !isCompleted && isArchived) return AdStatus.INACTIVE

  return AdStatus.INACTIVE
}

const PublishedSwitch = ({ record }: { record: { id: number; is_published?: boolean } }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [checked, setChecked] = useState(!!record.is_published)

  useEffect(() => {
    setChecked(!!record.is_published)
  }, [record.is_published])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked
    setChecked(value)
    const data: { is_published: boolean; is_archived?: boolean } = { is_published: value }
    if (value) data.is_archived = false
    dataProvider
      .update('structs', {
        id: record.id,
        data,
        previousData: record,
      })
      .then(() => {
        refresh()
        notify(doNotTranslate('Saved'), 'success')
      })
      .catch(() => {
        setChecked(!!record.is_published)
        notify(doNotTranslate('Error updating published state'), 'error')
      })
  }

  return <Switch key={record.id} checked={checked} onChange={handleChange} />
}

const ArchivedSwitch = ({ record }: { record: { id: number; is_archived?: boolean } }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [checked, setChecked] = useState(!!record.is_archived)

  useEffect(() => {
    setChecked(!!record.is_archived)
  }, [record.is_archived])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked
    setChecked(value)
    const data: { is_archived: boolean; is_published?: boolean } = { is_archived: value }
    if (value) data.is_published = false
    dataProvider
      .update('structs', {
        id: record.id,
        data,
        previousData: record,
      })
      .then(() => {
        refresh()
        notify(doNotTranslate('Saved'), 'success')
      })
      .catch(() => {
        setChecked(!!record.is_archived)
        notify(doNotTranslate('Error updating archived state'), 'error')
      })
  }

  return <Switch key={record.id} checked={checked} onChange={handleChange} />
}

/** Fixed-width column styles for Datagrid (headerStyle/cellStyle used by framework at runtime) */
const fixedCol = (width: number) =>
  ({
    headerStyle: { width, flex: '0 0 auto' as const },
    cellStyle: { width, flex: '0 0 auto' as const },
  } as Record<string, unknown>)

export const StructList = (props) => {
  const { currentTypeId, currentType, allStructTypes } = useCurrentStructType(props.match.params.structType)
  const isSingularList = currentTypeId === 'singular'
  const singularStructTypes = allStructTypes?.filter((t) => t.singular)
  const isAdStruct = AdStructTypes.includes(currentTypeId)

  if (isSingularList && !singularStructTypes) {
    return <FullScreenLoading />
  }

  return (
    <List
      key={isSingularList ? 'structs-singular' : props.match.params.structType}
      hasSearch={true}
      {...props}
      showNoResultsPage={false}
      filter={
        isSingularList
          ? {
              type: null,
              type__in: singularStructTypes!.map((t) => t.id).join(','),
              show_archived: 1,
            }
          : undefined
      }
      actions={
        <StructListActions
          currentType={currentType}
          currentTypeId={currentTypeId}
          singularStructTypes={singularStructTypes}
        />
      }
      filters={!isSingularList ? <StructFilter structTypes={allStructTypes} /> : undefined}
      breadCrumbs={
        <StructsBreadcrumbs
          currentTypeId={currentTypeId}
          currentType={isSingularList ? undefined : currentType}
          mode="list"
        />
      }
      basePath={isSingularList ? '/structs/singular' : `/structs/${currentTypeId}`}
    >
      <Datagrid>
        <FunctionField
          label="Published"
          render={(record) => <PublishedSwitch record={record} />}
          sortable={false}
          {...fixedCol(70)}
        />
        {isSingularList && (
          <FunctionField
            label="Type"
            render={(record) => allStructTypes?.find((t) => t.id === record.type)?.title ?? record.type}
          />
        )}
        {!isSingularList && <TextField source="key" />}
        <TextField source="description" />
        {!isSingularList && isAdStruct && <FunctionField label="Status" render={(record) => getStatus(record)} />}
        {!isSingularList && isAdStruct && (
          <BooleanField source="data.top_level_fields.is_completed" label="Completed" />
        )}
        {isSingularList ? (
          <FunctionField
            label="Actions"
            render={(record) => <SingularRowActions record={record} />}
            {...fixedCol(170)}
          />
        ) : (
          <EditOrImportButton
            source="actions"
            label="Actions"
            resource="structs"
            allowDuplicate={false}
            sortable={false}
            allowEdit={true}
            allowDelete={true}
            {...fixedCol(170)}
          />
        )}
        {isSingularList && (
          <FunctionField
            label="Archived"
            render={(record) => <ArchivedSwitch record={record} />}
            sortable={false}
            {...fixedCol(70)}
          />
        )}
      </Datagrid>
    </List>
  )
}
